import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import MainApp from '../apps/MainApp';
import logger from '../utils/logger';
const AdminApp = React.lazy(() => import('../apps/AdminApp'));
const StatsApp = React.lazy(() => import('../apps/StatsApp'));
const BillingApp = React.lazy(() => import('../apps/BillingApp'));
import { useRole } from '../contexts/RoleContext';
import { useAuth } from '../contexts/AuthContext';
import { AuthDialog } from './AuthDialog';
import GoogleSignInButton from './GoogleSignInButton';
import AppleSignInButton from './AppleSignInButton';
import { getRedirectResult } from 'firebase/auth';
import { auth } from '../firebase/init';
import LoadingScreen from './LoadingScreen';

// Role-based authorized app component
const AuthorizedApp: React.FC = () => {
  const { 
    userRole, 
    loading: roleLoading, 
    hasMinimumRole, 
    userPermissions,
    isWebsiteAdmin,
    getManagedOrganizations,
    getManagedTeams
  } = useRole();
  const { user, loading: authLoading } = useAuth(); // Get both user and loading state
  const [roleStateReady, setRoleStateReady] = React.useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);
  
  // Ensure role state has propagated before making authorization decisions
  React.useEffect(() => {
    if (!roleLoading) {
      // Role loading is complete (regardless of whether a role was found)
      setRoleStateReady(true);
    } else {
      setRoleStateReady(false);
    }
  }, [roleLoading]);

  // Determine which app to render based on URL path (single domain)
  const getAppType = () => {
    const pathname = window.location.pathname;
    
    // Check path patterns first
    if (pathname.startsWith('/admin')) {
      return 'admin';
    }
    if (pathname.startsWith('/stats')) {
      return 'stats';
    }
    if (pathname.startsWith('/billing')) {
      return 'billing';
    }
    
    // Default to main app
    return 'main';
  };

  const appType = getAppType();
  
  // No cross-domain auth needed with single domain approach
  
  // Handle Google Sign-In redirect results with timeout
  React.useEffect(() => {
    const handleRedirectResult = async () => {
      logger.log('🔐 Starting redirect result handler...');
      try {
        logger.log('🔐 Checking for redirect result...');
        logger.log('🔐 Auth state before getRedirectResult:', auth.currentUser?.email || 'no user');
        
        // Add timeout to prevent hanging
        logger.log('🔐 Calling getRedirectResult with 10 second timeout...');
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('getRedirectResult timeout')), 10000);
        });
        
        const result: any = await Promise.race([
          getRedirectResult(auth),
          timeoutPromise
        ]);
        
        logger.log('🔐 getRedirectResult completed');
        logger.log('🔐 Redirect result:', result);
        
        if (result && result.user) {
          logger.log('✅ Redirect sign-in successful:', result.user.email);
          logger.log('✅ User object:', result.user);
          logger.log('✅ User UID:', result.user.uid);
          logger.log('✅ Auth current user after redirect:', auth.currentUser?.email);
        } else {
          logger.log('ℹ️ No redirect result found');
          logger.log('ℹ️ Result value:', result);
          logger.log('ℹ️ Auth current user after null result:', auth.currentUser?.email || 'no user');
        }
      } catch (error: any) {
        logger.error('❌ Redirect result error:', error);
        logger.error('❌ Error code:', error?.code);
        logger.error('❌ Error message:', error?.message);
        logger.error('❌ Full error object:', error);
        
        // If timeout, try alternative approach
        if (error?.message?.includes('timeout')) {
          logger.log('🔄 getRedirectResult timed out, checking auth state directly...');
          logger.log('🔄 Current auth user after timeout:', auth.currentUser?.email || 'no user');
          
          // Check URL for redirect parameters
          const urlParams = new URLSearchParams(window.location.search);
          logger.log('🔄 URL parameters:', Object.fromEntries(urlParams.entries()));
          
          // Look for Firebase Auth parameters
          if (window.location.hash || urlParams.get('code') || urlParams.get('state')) {
            logger.log('🔄 Found potential auth parameters in URL');
            logger.log('🔄 Hash:', window.location.hash);
            logger.log('🔄 Search:', window.location.search);
          }
        }
      }
    };
    
    // Small delay to ensure auth is fully initialized
    const timer = setTimeout(handleRedirectResult, 500);
    return () => clearTimeout(timer);
  }, [auth]);
  
  // Remove complex cross-domain token handling - use simple redirect flow
  
  // Show loading screen while auth or roles are still loading
  // Wait for auth to finish loading before making authorization decisions
  
  // Debug logging for loading states
  logger.log('🕐 Loading State Debug:', {
    authLoading,
    user: !!user,
    roleLoading,
    roleStateReady,
    userRole,
    shouldShowLoading: authLoading || (user && roleLoading) || (user && !roleStateReady)
  });

  // Show loading while auth or roles are being determined
  if (authLoading || (user && roleLoading) || (user && !roleStateReady)) {
    logger.log('⏳ Showing loading screen due to:', {
      authLoading,
      userAndRoleLoading: user && roleLoading,
      roleStateNotReady: user && !roleStateReady
    });

    return <LoadingScreen />;
  }

  // Check if user is authenticated and has appropriate role for the site
  // IMPORTANT: Don't make authorization decisions while roles are still loading
  // IMPORTANT: For subdomains, wait for cross-domain auth to complete before deciding
  let isAuthorized = false;
  
  // Simple authorization logic for single domain
  switch (appType) {
    case 'main':
      // Main site is always accessible (public landing page)
      isAuthorized = true;
      break;
    case 'admin':
      // 1.7 management is site-admin only; coach access ships with 1.8.
      if (!user) {
        isAuthorized = false;
      } else {
        // If user exists, wait for role loading to complete, then check admin role or org/team admin roles
        const hasWebsiteAdminRole = isWebsiteAdmin();
        const managedOrgs = getManagedOrganizations();
        const managedTeams = getManagedTeams();
        const hasOrgTeamAdminRole = managedOrgs.length > 0 || managedTeams.length > 0;
        
        isAuthorized = !roleLoading && hasWebsiteAdminRole;
        logger.log('🔍 Admin Authorization Check:', {
          hasUser: !!user,
          userId: user?.uid,
          userEmail: user?.email,
          roleLoading,
          userRole,
          hasWebsiteAdminRole,
          managedOrgs,
          managedTeams,
          hasOrgTeamAdminRole,
          isAuthorized
        });
      }
      break;
    case 'stats':
    case 'billing':
      // Stats and Billing require any authenticated user
      if (!user) {
        isAuthorized = false;
      } else {
        // Any authenticated user is allowed
        isAuthorized = true;
        logger.log('🔍 Stats/Billing Authorization Check:', {
          hasUser: !!user,
          userId: user?.uid,
          userEmail: user?.email,
          isAuthorized
        });
      }
      break;
    default:
      isAuthorized = false;
  }

  // Debug logging for authentication issues
  logger.log('🔐 Auth Debug:', {
    appType,
    pathname: window.location.pathname,
    user: user?.uid,
    userEmail: user?.email,
    userRole: userRole,
    userRoleType: typeof userRole,
    hasMinRole: hasMinimumRole('admin'),
    isAuthorized,
    roleLoading,
    roleStateReady
  });
  
  // Detailed authorization debug
  logger.log('🔍 Authorization Debug:', {
    appType,
    hasUser: !!user,
    roleLoading,
    roleStateReady,
    userRole,
    authorizationSwitch: {
      isMain: appType === 'main',
      isAdmin: appType === 'admin',
      hasAdminRole: hasMinimumRole('admin'),
      finalAuthorization: isAuthorized
    }
  });


  // Show auth dialog for unauthorized users (single domain - no redirects needed)
  if (!isAuthorized) {
    // SAFETY CHECK: Main site should NEVER be unauthorized
    if (appType === 'main') {
      logger.error('🚨 CRITICAL ERROR: Main site is unauthorized - this should never happen!');
      // Show error instead of redirect loop
      return (
        <Box
          component="main"
          display="flex" 
          flexDirection="column"
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
          sx={{ bgcolor: 'background.default', textAlign: 'center' }}
          p={3}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            🚨 Configuration Error
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            Main site authorization failed. Please contact support.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
          >
            Reload Page
          </Button>
        </Box>
      );
    }

    // For protected pages, show authentication UI directly (no redirects needed)
    return (
      <Box
        component="main"
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ bgcolor: 'background.default', textAlign: 'center' }}
        p={3}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          🔐 Authentication Required
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          {!user ? 'Please sign in to access this area.' : 'You need admin privileges to access this area.'}
        </Typography>
        
        {/* Show authentication dialog */}
        <Box mt={3} display="flex" flexDirection="column" alignItems="center" gap={2}>
          <GoogleSignInButton
            onClick={() => {
              // Use popup-based auth (single domain - no redirect issues)
              import('firebase/auth').then(({ GoogleAuthProvider, signInWithPopup }) => {
                const provider = new GoogleAuthProvider();
                logger.log('🔄 Using popup-based auth on single domain');
                signInWithPopup(auth, provider).catch(error => {
                  logger.error('❌ Popup sign-in failed:', error);
                });
              });
            }}
            text="Sign in with Google"
            fullWidth={false}
            variant="dark"
          />
          
          <AppleSignInButton
            onClick={() => {
              // Use popup-based auth for Apple (single domain - no redirect issues)
              import('firebase/auth').then(({ OAuthProvider, signInWithPopup }) => {
                const provider = new OAuthProvider('apple.com');
                provider.addScope('email');
                provider.addScope('name');
                logger.log('🍎 Using popup-based Apple auth on single domain');
                signInWithPopup(auth, provider).catch(error => {
                  logger.error('❌ Apple popup sign-in failed:', error);
                });
              });
            }}
            text="Sign in with Apple"
            fullWidth={false}
            variant="black"
          />

          {/* Email accounts sign in through the same dialog as the home page */}
          {!user && (
            <Button variant="outlined" onClick={() => setEmailDialogOpen(true)}>
              Sign in with email
            </Button>
          )}
          
          <Button 
            variant="outlined" 
            onClick={() => window.location.href = '/'}
            sx={{ mt: 1 }}
          >
            Go to Home
          </Button>
        </Box>
        <AuthDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          auth={auth}
        />
      </Box>
    );
  }


  const renderApp = () => {
    // Suspense fallback for lazy-loaded components - simplified without background video
    const LazyFallback = <LoadingScreen />;
    
    switch (appType) {
      case 'main':
        return <MainApp />;
      case 'stats':
        return (
          <React.Suspense fallback={LazyFallback}>
            <StatsApp />
          </React.Suspense>
        );
      case 'billing':
        return (
          <React.Suspense fallback={LazyFallback}>
            <BillingApp />
          </React.Suspense>
        );
      case 'admin':
        return (
          <React.Suspense fallback={LazyFallback}>
            <AdminApp />
          </React.Suspense>
        );
      default:
        return <MainApp />;
    }
  };

  return (
    <div className="App">
      {renderApp()}
    </div>
  );
};

export default AuthorizedApp;

